import L from "leaflet";

document.addEventListener("turbo:load", () => {
  initializeDeDonorMap();
});

async function initializeDeDonorMap() {
  const mapElement = document.getElementById("map");
  if (!mapElement) return;

  console.log("Loading maps/de_donor_map.js");

  const eventId = mapElement.dataset.eventId;
  const showAmountDonated = mapElement.dataset.showAmountDonated === "true";
  const baseColor = (mapElement.dataset.baseColor || "#495057").trim();
  const hoverColor = (mapElement.dataset.hoverColor || "#FFA500").trim();
  const labels = (mapElement.dataset.legendLabels || "")
    .split(",")
    .map((l) => l.trim())
    .filter(Boolean);

  // Compute thresholds
  const thresholds = labels.length
    ? labels.map((label) => {
        if (label.includes("+")) return parseInt(label, 10);
        if (label.includes("to")) return parseInt(label.split("to")[0].trim(), 10);
        return 0;
      })
    : [0];
  const step = labels.length > 1 ? 1.0 / (labels.length - 1) : 1.0;

  const getOpacityForCount = (count) => {
    let index = 0;
    for (let i = 0; i < thresholds.length; i++) {
      if (count >= thresholds[i]) index = i;
    }
    return Number((index * step).toFixed(2));
  };

  // Map setup
  const map = L.map(mapElement, {
    center: [38.9, -75.5],
    zoom: 8,
    minZoom: 8,
    maxZoom: 12,
    maxBounds: [
      [36.96745, -78.28857],
      [39.96028, -72.94922],
    ],
    maxBoundsViscosity: 0.05,
  });

  L.tileLayer(
    "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    {
      opacity: 0.55,
      attribution:
        '&copy; <a href="https://carto.com/">CARTO</a> contributors &mdash; ' +
        '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
    }
  ).addTo(map);

  const mergedGeoPath = "/geom/merged_zipcodes.geojson?v=1";
  const donationsPath = `/ajax/event_donations_by_zip?event_id=${eventId}`;

  try {
    // Fetch GeoJSON + Donations in parallel
    const [geoRes, donationsRes] = await Promise.all([
      fetch(mergedGeoPath, { cache: "force-cache" }),
      fetch(donationsPath, { cache: "no-store" }),
    ]);

    const geo = await geoRes.json();
    const donationsByZip = await donationsRes.json();

    renderMap(map, geo, donationsByZip, { baseColor, hoverColor, showAmountDonated, getOpacityForCount });
  } catch (err) {
    console.error("Failed to load map data:", err);
  }
}

function renderMap(map, geo, donationsByZip, opts) {
  const { baseColor, hoverColor, showAmountDonated, getOpacityForCount } = opts;

  // Update stats dynamically
  geo.features.forEach((f) => {
    const zips = f.properties.childZips || [];
    let count = 0;
    let amount_cents = 0;
    zips.forEach((z) => {
      const d = donationsByZip[z];
      if (d) {
        count += d.count || 0;
        amount_cents += d.amount_cents || 0;
      }
    });
    f.properties.stats = { count, amount_cents };
  });

  const layer = L.geoJSON(geo, {
    style: (feature) => {
      const count = feature.properties?.stats?.count || 0;
      return {
        color: "#696969",
        weight: 1,
        fillColor: baseColor,
        fillOpacity: getOpacityForCount(count),
      };
    },
    onEachFeature: (feature, layer) => {
      const stats = feature.properties.stats || { count: 0, amount_cents: 0 };
      const zips = feature.properties.childZips.join(", ");
      const locs = feature.properties.childLocations.join(", ");
      const amount = (stats.amount_cents / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });

      const html = `
        <h6 class="fw-bold mb-1">${zips} (${locs.toUpperCase()})</h6>
        <div class="mb-2 text_gray">
          <p class='fs-6'>Number of donations: ${stats.count}</p>
          ${showAmountDonated ? `<p class='fs-6'>Amount raised: ${amount}</p>` : ""}
        </div>
      `;

      layer.bindTooltip(html, { sticky: true });

      // Hover styling
      layer.on("mouseover", function (e) {
        e.target.setStyle({ fillColor: hoverColor, fillOpacity: 0.5 });
        e.target.bringToFront();
      });

      // Reset styling
      layer.on("mouseout", function (e) {
        const count = feature.properties?.stats?.count || 0;
        e.target.setStyle({
          fillColor: baseColor,
          fillOpacity: getOpacityForCount(count),
        });
        e.target.bringToFront();
      });

      // Click-to-zoom
      layer.on("click", function (e) {
        map.fitBounds(e.target.getBounds(), { maxZoom: 12, padding: [20, 20] });
      });
    },
  }).addTo(map);

  map.fitBounds(layer.getBounds(), { padding: [10, 10] });
}